/**
 * Main application component
 *
 * Displays authentication status and provides sign in/out functionality.
 * Works with both mock and OIDC authentication providers.
 */

import React from 'react';
import { useAuth, useUser, useProviderLogout } from './auth/hooks';
import { getConfig } from './config';
import { EnvIndicator } from './components/EnvIndicator';
import { ApiStatus } from './components/ApiStatus';
import { logAuthEvent, getStoredCorrelationId, generateCorrelationId } from './auth/logging';
import { useDevModeConfig } from './hooks/useDevModeConfig.js';
import { DevModeSettings } from './components/DevModeSettings';
import { ClientList } from './components/ClientList';
import {
  Button,
  Card,
  Stack,
  Flex,
  useTheme,
  spacing,
} from '@fti/design-system';

export const App: React.FC = () => {
  const auth = useAuth();
  const user = useUser();
  const config = getConfig();
  const { signoutRedirect } = useProviderLogout();
  const devModeConfig = useDevModeConfig();
  const { theme, setTheme } = useTheme();

  // Force light theme regardless of system preference
  React.useEffect(() => {
    setTheme('light');
  }, [setTheme]);

  // Get the effective API URL - uses runtime override in dev mode
  const effectiveApiUrl = devModeConfig.getEffectiveApiUrl();

  // Log authentication errors when they occur
  React.useEffect(() => {
    if (auth.error) {
      const correlationId = getStoredCorrelationId() || generateCorrelationId();
      logAuthEvent('auth_error', 'Authentication error occurred', {
        correlationId,
        errorType: auth.error.name,
        error: auth.error
      });
    }
  }, [auth.error]);

  // Handle sign in with logging
  const handleSignIn = () => {
    const correlationId = getStoredCorrelationId() || generateCorrelationId();
    logAuthEvent('auth_attempt', 'User initiated sign-in flow', {
      correlationId
    });
    auth.signinRedirect();
  };

  // Determine user display label
  const userLabel = auth.isAuthenticated
    ? user?.email || 'Signed in'
    : auth.isLoading
    ? 'Checking session…'
    : auth.error
    ? 'Auth error (check environment configuration)'
    : 'Signed out';

  // Check if we're in production mode
  const isProdEnvironment = config.name === 'prod';

  // Theme-aware colors from design system
  const bgColor = theme.colors.background.default;
  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const successColor = theme.colors.semantic.success.default;
  const errorColor = theme.colors.semantic.error.default;

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: bgColor,
        color: textColor,
        padding: spacing.lg,
      }}
    >
      {/* Production Warning Banner */}
      {isProdEnvironment && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            padding: `${spacing.sm} ${spacing.md}`,
            background: errorColor,
            color: theme.colors.text.inverse,
            textAlign: 'center',
            fontSize: '0.85rem',
            fontWeight: 600,
            zIndex: 9999,
          }}
        >
          ⚠️ PRODUCTION ENVIRONMENT - Actions affect real data
        </div>
      )}

      <Card variant="elevated" style={{ maxWidth: '480px', width: '100%' }}>
        <Card.Body>
          <Stack gap="md">
            {/* Header */}
            <header>
              <Flex justify="between" align="center" style={{ marginBottom: spacing.sm }}>
                <h1
                  style={{
                    fontSize: '1.4rem',
                    fontWeight: 600,
                    margin: 0,
                    letterSpacing: '0.03em',
                    color: textColor,
                  }}
                >
                  Vibe Template
                </h1>
                <Flex align="center" gap="sm">
                  <DevModeSettings />
                  <EnvIndicator
                    environmentName={config.name}
                    displayName={config.displayName}
                    isMock={config.isMock}
                    apiTarget={devModeConfig.isDevModeAvailable ? devModeConfig.config.apiTarget : undefined}
                  />
                </Flex>
              </Flex>
              <p style={{ margin: 0, marginTop: spacing.xs, fontSize: '0.95rem', color: textColor, fontWeight: 500 }}>
                {auth.isAuthenticated && user?.email
                  ? `Welcome, ${user.email}`
                  : auth.isLoading
                  ? 'Welcome (checking session...)'
                  : 'Welcome'}
              </p>
              <p style={{ margin: 0, fontSize: '0.9rem', color: mutedColor }}>
                Multi-environment authentication
              </p>
            </header>

            {/* Status Info */}
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: `${spacing.xs} ${spacing.sm}`,
                margin: 0,
                fontSize: '0.9rem',
              }}
            >
              <dt style={{ color: mutedColor }}>User</dt>
              <dd style={{ margin: 0 }}>
                {auth.isLoading && <span style={{ color: textColor }}>Loading…</span>}
                {auth.isAuthenticated && user && (
                  <span style={{ color: successColor }}>
                    {user.email || 'Signed in'}
                  </span>
                )}
                {!auth.isAuthenticated && !auth.isLoading && (
                  <span style={{ color: errorColor }}>{userLabel}</span>
                )}
              </dd>

              <dt style={{ color: mutedColor }}>Status</dt>
              <dd style={{ margin: 0 }}>
                {auth.isAuthenticated && (
                  <span style={{ color: successColor }}>Authenticated</span>
                )}
                {auth.isLoading && (
                  <span style={{ color: textColor }}>Checking session…</span>
                )}
                {auth.error && <span style={{ color: errorColor }}>Error</span>}
                {!auth.isAuthenticated && !auth.isLoading && !auth.error && (
                  <span style={{ color: errorColor }}>Signed out</span>
                )}
              </dd>

              {auth.error && (
                <>
                  <dt style={{ color: mutedColor }}>Detail</dt>
                  <dd style={{ margin: 0, color: errorColor }}>
                    {auth.error.message ||
                      'Authentication failed (check environment configuration)'}
                  </dd>
                </>
              )}

              {user?.name && user.name !== user.email && (
                <>
                  <dt style={{ color: mutedColor }}>Name</dt>
                  <dd style={{ margin: 0, color: textColor }}>
                    {user.name}
                  </dd>
                </>
              )}
            </dl>

            {/* Action Buttons */}
            <Flex gap="sm">
              {!auth.isAuthenticated && !auth.isLoading && (
                <Button variant="solid" color="primary" size="sm" onClick={handleSignIn}>
                  Sign in
                </Button>
              )}
              {auth.isAuthenticated && (
                <Button variant="outline" color="error" size="sm" onClick={() => signoutRedirect()}>
                  Sign out
                </Button>
              )}
              {auth.error && (
                <Button variant="outline" color="error" size="sm" onClick={handleSignIn}>
                  Retry sign in
                </Button>
              )}
            </Flex>

            {/* Error Details */}
            {auth.error && (
              <div
                style={{
                  fontSize: '0.8rem',
                  color: errorColor,
                  background: `${theme.colors.semantic.error.light}20`,
                  border: `1px solid ${theme.colors.semantic.error.light}`,
                  borderRadius: '0.45rem',
                  padding: spacing.sm,
                }}
              >
                {auth.error.message?.includes('redirect_uri') ? (
                  <>
                    <strong>OAuth Callback URL Error:</strong> The redirect URI (http://localhost:3000)
                    is not configured in Cognito's allowed callback URLs.
                    <br /><br />
                    <em>Fix: Add http://localhost:3000 to the Cognito user pool's "Allowed callback URLs" via AWS Console or Terraform.</em>
                  </>
                ) : (
                  <>
                    Check environment configuration: VITE_ENVIRONMENT, authority, client ID,
                    redirect URLs, scopes.
                  </>
                )}
              </div>
            )}

            {/* API Connection Status */}
            <ApiStatus apiUrl={effectiveApiUrl} autoRefreshMs={30000} />

            {/* Client List - only show when authenticated */}
            {auth.isAuthenticated && (
              <ClientList />
            )}
          </Stack>
        </Card.Body>
      </Card>
    </main>
  );
};
